import { reactive, computed } from 'vue';
import type { AxiosInstance } from 'axios';

export type FlagSource = Record<string, FlagValue>;

export type FlagValue = {
  sourceURL?: string;
  enabled: boolean;
  [key: string]: unknown;
};

export interface FeatureFlagsConfig {
  [key: string]: FlagValue;
}

class FeatureFlag {
  private _flags = reactive<FlagSource>({});
  private http?: AxiosInstance;

  setHttpAdapter(adapter?: AxiosInstance) {
    this.http = adapter;
  }

  load(source: FlagSource | string) {
    Object.assign(this._flags, source);
  }

  private async fetch(url: string) {
    if (this.http) {
      const res = await this.http.get(url);
      return res.data;
    }

    const res = await fetch(url);
    return await res.json();
  }

  async initiate() {
    for (const key in this._flags) {
      const localFlag = this._flags[key];

      if (localFlag.sourceURL) {
        const enabled = await this.fetch(localFlag.sourceURL);

        Object.assign(this._flags[key], {
          ...localFlag,
          enabled,
        });
      }
    }
  }

  isEnabled(key: string): boolean {
    const flag = this._flags[key];
    if (!flag) return false;

    const enabled = flag.enabled;
    return Boolean(enabled);
  }

  setFlag(key: string, value: FlagValue) {
    this._flags[key] = value;
  }

  get all() {
    return computed(() => this._flags);
  }
}

export const featureFlag = new FeatureFlag();
